type DebugOptionBase = {
  key: string;
  label: string;
  description: string;
  refreshOnChange?: boolean;
  hiddenFromCommandMenu?: boolean;
};

type DebugOptionToggle = DebugOptionBase & {
  type?: "toggle";
};

type DebugOptionText = DebugOptionBase & {
  type: "text";
  placeholder?: string;
  defaultValue?: string;
  showWhen?: string;
};

type DebugOptionButton = DebugOptionBase & {
  type: "button";
  buttonLabel?: string;
  isToggleable?: boolean;
};

type DebugOptionSelect = DebugOptionBase & {
  type: "select";
  options: { value: string; label: string }[];
  defaultValue?: string;
};

export type DebugOption =
  | DebugOptionToggle
  | DebugOptionText
  | DebugOptionButton
  | DebugOptionSelect;

type DebugOptionKey = DebugOption["key"];

export function isDebugOptionEnabled(debugOption: DebugOptionKey): boolean {
  return (
    typeof window !== "undefined" &&
    localStorage.getItem(debugOption) === "true"
  );
}

export const DEBUG_OPTIONS: DebugOption[] = [
  {
    key: "replo.debug.overlayIsVisible",
    label: "Agent Debugging Overlays",
    description:
      "Toggle debug overlays — session links, tool I/O, billing data, and the floating debug menu (Fn+F1)",
    type: "button",
    isToggleable: true,
  },
  {
    key: "replo.debug.reduxLoggingToConsole",
    label: "Redux Logging to Console",
    description:
      "Logs all Redux actions and state changes to the browser console",
  },
  {
    key: "replo.debug.useBackendStaging",
    label: "Use Backend Staging",
    description:
      "Routes backend calls to staging and uses the staging Daytona snapshot for new sandboxes.",
    refreshOnChange: true,
  },
  {
    key: "replo.debug.stagingNamespace",
    label: "Staging namespace",
    description:
      "Routes Publisher, authentication, files, sandboxes, and the runtime stack to the same named staging deployment. Blank uses shared staging.",
    type: "text",
    placeholder: "staging",
    showWhen: "replo.debug.useBackendStaging",
  },
  {
    key: "replo.debug.useBackendStagingFly",
    label: "Use Backend Staging (Fly)",
    description:
      "Override: routes publisher calls to the Fly staging app until AWS cutover is complete. Takes precedence over Use Backend Staging when both are on.",
  },
  {
    key: "replo.debug.useShopifyDebugTools",
    label: "Use Shopify Debugging Tools",
    description:
      "Enables Shopify-specific debugging tools in the preview iframe",
  },
  {
    key: "replo.debug.enablePosthogSessionRecording",
    label: "Enable Posthog Session Recording",
    description: "Starts a Posthog session recording for the current session",
  },
  {
    key: "replo.debug.copyComponentJsonMinify",
    label: "Copy Component JSON as Minified",
    description:
      "Copies component JSON to clipboard as a single line instead of pretty-printed",
  },
  {
    key: "replo.debug.jsonActionPlayer",
    label: "JSON Action Player",
    description:
      "Shows the JSON Action Player button in the canvas controls toolbar",
  },
  {
    key: "replo.debug.v2.logChatStreamEventsToConsole",
    label: "Log Chat Stream Events to Console",
    description:
      "Logs all V2 chat stream events to the browser console for debugging",
  },
  {
    key: "replo.debug.showNextDevServerLogs",
    label: "Show Next Dev Server Logs",
    description:
      "Shows the Next.js dev server log panel for Replo-hosted projects",
  },
  {
    key: "replo.debug.showFailedPaymentState",
    label: "Show Failed Payment State",
    description:
      "Forces the failed payment UI to display for testing billing flows",
  },
  {
    key: "replo.debug.carouselDebug",
    label: "Carousel Debug Events",
    description:
      "Enables debug event logging for Replo V1 carousel components to help troubleshoot carousel behavior",
  },
];
